// Coerces arbitrary resume payloads into the exact shape the export templates
// expect.
//
// resumeData reaches the exporter from several places that do not agree on
// types: localStorage snapshots written by older builds, cloud documents,
// JSON Resume imports (`skills` as objects, `highlights` as arrays), PDF
// imports and AI-generated content. @react-pdf/renderer throws hard on any of
// those — "Objects are not valid as a React child", "desc.split is not a
// function" — which surfaces to the user as an opaque minified React error at
// the exact moment they click Download.
//
// Everything here degrades to empty text rather than throwing.

const TEXT_KEYS = ['name', 'title', 'text', 'label', 'value', 'description'];

/** Any value -> a safe string. Arrays become newline-joined lines. */
export const toText = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) return value.map(toText).filter(Boolean).join('\n');
    if (typeof value === 'object') {
        // JSON Resume and similar schemas nest the readable part one level down.
        for (const key of TEXT_KEYS) {
            if (typeof value[key] === 'string' && value[key]) return value[key];
        }
        return '';
    }
    return '';
};

const toList = (value) => (Array.isArray(value) ? value.filter(item => item !== null && item !== undefined) : []);

const BUILT_IN_SECTIONS = ['experience', 'education', 'skills'];

/**
 * Returns a defensive copy of `resumeData` with every field forced to the type
 * the templates assume. Section ids are normalised to strings on both
 * `customSections` and `sectionOrder` so a custom section can't silently vanish
 * from the export just because its id survived a JSON round-trip as `"99"`
 * instead of `99`.
 */
export const sanitizeResumeData = (resumeData) => {
    const source = resumeData && typeof resumeData === 'object' ? resumeData : {};
    const personal = source.personal && typeof source.personal === 'object' ? source.personal : {};

    const customSections = toList(source.customSections)
        .filter(section => typeof section === 'object')
        .map((section, index) => ({
            id: String(section.id ?? `custom-${index}`),
            title: toText(section.title),
            icon: toText(section.icon) || 'Star',
            items: toList(section.items)
                .filter(item => typeof item === 'object')
                .map((item, itemIndex) => ({
                    id: String(item.id ?? `${index}-${itemIndex}`),
                    title: toText(item.title),
                    subtitle: toText(item.subtitle),
                    subtext: toText(item.subtext),
                    description: toText(item.description)
                }))
        }));

    const knownCustomIds = new Set(customSections.map(section => section.id));

    const rawOrder = toList(source.sectionOrder).map(id => String(id));
    const order = rawOrder.filter(id => BUILT_IN_SECTIONS.includes(id) || knownCustomIds.has(id));

    // Never drop a built-in section just because an old sectionOrder predates it.
    BUILT_IN_SECTIONS.forEach(id => {
        if (!order.includes(id)) order.push(id);
    });

    return {
        personal: {
            fullName: toText(personal.fullName),
            email: toText(personal.email),
            phone: toText(personal.phone),
            location: toText(personal.location),
            summary: toText(personal.summary)
        },
        experience: toList(source.experience)
            .filter(entry => typeof entry === 'object')
            .map((entry, index) => ({
                id: String(entry.id ?? `exp-${index}`),
                role: toText(entry.role),
                company: toText(entry.company),
                location: toText(entry.location),
                startDate: toText(entry.startDate),
                endDate: toText(entry.endDate),
                description: toText(entry.description)
            })),
        education: toList(source.education)
            .filter(entry => typeof entry === 'object')
            .map((entry, index) => ({
                id: String(entry.id ?? `edu-${index}`),
                degree: toText(entry.degree),
                school: toText(entry.school),
                location: toText(entry.location),
                year: toText(entry.year)
            })),
        skills: toList(source.skills).map(toText).filter(Boolean),
        customSections,
        sectionOrder: order
    };
};

export default sanitizeResumeData;
